import React, { useCallback, useEffect, useState } from 'react';
import { Table, Tag, Button, DatePicker, Empty, Space, Dropdown } from 'antd';
import {
  BookOutlined, CheckCircleOutlined, FileExcelOutlined, FileTextOutlined, SearchOutlined,
  CheckCircleFilled, WarningFilled, DownloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import PageHeader from '../../components/PageHeader';
import axiosInstance from '../../variables/api/axiosInstance';
import { can, formatDate, formatMontant } from '../../variables/constants';
import { HAB } from '../../variables/habilitations';
import { libellesTraduits } from '../../i18n/libelles';
import BoutonFlottant, { STYLE_BOUTON_FLOTTANT } from '../../components/BoutonFlottant';
import {
  GREEN, ROUGE, Montant, equilibre, telecharger, LIBELLE_OPERATION,
} from './commun';

const { RangePicker } = DatePicker;

const COULEUR_STATUT = { VALIDEE: 'green', BROUILLON: 'orange', ANNULEE: 'red' };
const LIBELLE_STATUT = libellesTraduits('comptabilite:statutsEcriture');

const totalSens = (lignes, sens) =>
  (lignes || []).filter((l) => l.sens === sens).reduce((t, l) => t + Number(l.montant || 0), 0);

/**
 * Écritures comptables — une ligne par écriture, dépliable sur ses lignes au débit
 * et au crédit. Chaque encaissement, paiement ou préfinancement produit la sienne,
 * d'après son schéma (menu Comptabilité → Paramétrage des écritures).
 */
export default function EcrituresComptables() {
  const { t } = useTranslation('comptabilite');
  const [periode, setPeriode] = useState([dayjs().startOf('month'), dayjs()]);
  const [ecritures, setEcritures] = useState([]);
  const [total, setTotal] = useState(null);
  const [page, setPage] = useState({ courante: 1, taille: 25 });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(null);

  const bornes = () => ({
    debut: periode?.[0]?.format('YYYY-MM-DD'),
    fin: periode?.[1]?.format('YYYY-MM-DD'),
  });

  const charger = useCallback(async (courante = 1, taille = page.taille) => {
    const { debut, fin } = bornes();
    if (!debut || !fin) {
      toast.info(t('ecritures.messages.choisirPeriode'));
      return;
    }
    setLoading(true);
    try {
      const r = await axiosInstance.get('/v1/comptabilite/ecritures/periode', {
        params: { debut, fin, page: courante - 1, size: taille },
      });
      setEcritures(r.data?.data || []);
      setTotal(r.data?.totalElements ?? (r.data?.data || []).length);
      setPage({ courante, taille });
    } catch (e) {
      setEcritures([]);
      setTotal(0);
      toast.error(e?.response?.data?.message || t('ecritures.messages.chargementImpossible'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periode, page.taille, t]);

  // Premier affichage : le mois en cours.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { charger(1); }, []);

  const valider = async (id) => {
    try {
      await axiosInstance.patch(`/v1/comptabilite/ecritures/${id}/valider`);
      toast.success(t('ecritures.messages.validee'));
      charger(page.courante);
    } catch (e) {
      toast.error(e?.response?.data?.message || t('ecritures.messages.validationImpossible'));
    }
  };

  const exporter = async (format) => {
    const { debut, fin } = bornes();
    if (!debut || !fin) return;
    setExporting(format);
    try {
      const r = await axiosInstance.get(`/v1/comptabilite/export/journal.${format}`, {
        params: { debut, fin }, responseType: 'blob',
      });
      telecharger(r.data, `ecritures_comptables_${debut}_${fin}.${format}`);
    } catch {
      toast.error(t('ecritures.messages.exportImpossible', { format: format.toUpperCase() }));
    } finally {
      setExporting(null);
    }
  };

  const peutValider = can(HAB.ECRITURE_VALIDER);

  const colonnes = [
    { title: t('ecritures.colonnes.date'), dataIndex: 'dateEcriture', width: 110, render: (v) => formatDate(v) },
    { title: t('ecritures.colonnes.reference'), dataIndex: 'numeroEcriture', width: 170,
      render: (v) => <span style={{ fontWeight: 700, color: GREEN, fontVariantNumeric: 'tabular-nums' }}>{v}</span> },
    { title: t('commun.libelle'), key: 'libelle',
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 500 }}>{r.libelle}</div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
            {LIBELLE_OPERATION[r.typeTransaction] || r.typeTransaction}
            {r.sinistreNumeroLocal && <> · {t('ecritures.sinistre', { numero: r.sinistreNumeroLocal })}</>}
          </div>
        </div>
      ) },
    { title: t('commun.journal'), dataIndex: 'journalCode', width: 90, align: 'center',
      render: (v) => (v ? <Tag color="blue" style={{ borderRadius: 20, fontWeight: 600 }}>{v}</Tag> : '—') },
    { title: t('ecritures.colonnes.totalDebit'), key: 'debit', width: 140, align: 'right',
      render: (_, r) => <Montant valeur={totalSens(r.lignes, 'DEBIT')} gras /> },
    { title: t('ecritures.colonnes.totalCredit'), key: 'credit', width: 140, align: 'right',
      render: (_, r) => <Montant valeur={totalSens(r.lignes, 'CREDIT')} gras /> },
    { title: t('commun.statut'), dataIndex: 'statut', width: 110,
      render: (v) => <Tag color={COULEUR_STATUT[v]} style={{ borderRadius: 20 }}>{LIBELLE_STATUT[v] || v}</Tag> },
    { title: '', key: 'actions', width: 100, align: 'right',
      render: (_, r) => (r.statut === 'BROUILLON' && peutValider ? (
        <Button size="small" icon={<CheckCircleOutlined />}
          style={{ borderColor: GREEN, color: GREEN, borderRadius: 6 }}
          onClick={(e) => { e.stopPropagation(); valider(r.ecritureTrackingId); }}>
          {t('ecritures.valider')}
        </Button>
      ) : null) },
  ];

  const renderLignes = (ecriture) => {
    const lignes = ecriture.lignes || [];
    const debit = totalSens(lignes, 'DEBIT');
    const credit = totalSens(lignes, 'CREDIT');
    const ok = equilibre(debit, credit);
    const colonnesLignes = [
      { title: t('commun.journal'), key: 'j', width: 90,
        render: () => <Tag color="blue" style={{ borderRadius: 20 }}>{ecriture.journalCode || '—'}</Tag> },
      { title: t('commun.compte'), dataIndex: 'numeroCompte', width: 120,
        render: (v) => <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{v}</span> },
      { title: t('commun.libelle'), key: 'l', render: (_, l) => l.libelleLigne || l.libelleCompte },
      { title: t('commun.debit'), key: 'd', width: 150, align: 'right',
        render: (_, l) => <Montant valeur={l.sens === 'DEBIT' ? l.montant : 0} /> },
      { title: t('commun.credit'), key: 'c', width: 150, align: 'right',
        render: (_, l) => <Montant valeur={l.sens === 'CREDIT' ? l.montant : 0} /> },
    ];
    return (
      <div style={{ background: '#f6faf7', borderRadius: 10, padding: 12 }}>
        <Table size="small" rowKey={(_, i) => i} dataSource={lignes} columns={colonnesLignes}
          pagination={false}
          summary={() => (
            <Table.Summary.Row style={{ fontWeight: 700 }}>
              <Table.Summary.Cell index={0} colSpan={3}>
                {ok
                  ? <span style={{ color: GREEN }}><CheckCircleFilled /> {t('commun.equilibree')}</span>
                  : <span style={{ color: ROUGE }}><WarningFilled /> {t('ecritures.desequilibree')}</span>}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right">{formatMontant(debit)}</Table.Summary.Cell>
              <Table.Summary.Cell index={2} align="right">{formatMontant(credit)}</Table.Summary.Cell>
            </Table.Summary.Row>
          )} />
      </div>
    );
  };

  return (
    <Layout title={t('ecritures.titre')}>
      <PageHeader
        titre={t('ecritures.titre')}
        icone={<BookOutlined />}
        compte={total}
        unite={t('ecritures.unite')}
        unitePluriel={t('ecritures.unitePluriel')}
        precision={periode?.[0] && periode?.[1]
          ? t('ecritures.periode', { debut: periode[0].format('DD/MM/YYYY'), fin: periode[1].format('DD/MM/YYYY') }) : undefined}
      />

      <div className="cb-card" style={{ marginBottom: 16 }}>
        <Space wrap size={12}>
          <RangePicker value={periode} onChange={setPeriode} format="DD/MM/YYYY"
            placeholder={[t('commun.dateDebut'), t('commun.dateFin')]} allowClear={false} />
          <Button type="primary" icon={<SearchOutlined />} onClick={() => charger(1)} loading={loading}
            style={{ background: GREEN, borderColor: GREEN, borderRadius: 8 }}>
            {t('ecritures.filtrer')}
          </Button>
          {/* UN SEUL BOUTON AVEC SES OPTIONS, comme les quatre autres ecrans de la
              comptabilite - mais PAS avec `TableExportButton`.

              Ce composant fabrique le fichier dans le navigateur, a partir des lignes du
              tableau. Les deux exports de cet ecran viennent du SERVEUR, et ne sont pas
              des vidages de tableau : l'Excel porte deux feuilles, « Journal » et
              « Recap mensuel » ; le CSV est le fichier d'import du logiciel comptable
              legal - separateur point-virgule, decimales francaises, BOM UTF-8. Les
              refabriquer ici les remplacerait par une seule feuille a plat, et par un CSV
              que le logiciel comptable refuserait.

              D'ou la meme FORME - un bouton, un menu, le coin bas droit - sans le meme
              moteur. `BoutonFlottant` pose le coin et la reserve de place en bas de page,
              sans quoi la derniere ligne du tableau passe sous le bouton. */}
          <BoutonFlottant>
            <Dropdown
              placement="topRight"
              menu={{
                items: [
                  {
                    key: 'xlsx',
                    icon: <FileExcelOutlined style={{ color: '#107c41' }} />,
                    label: t('commun.exporterExcel'),
                    // Ce que chaque fichier contient : l'info vivait dans une infobulle
                    // qu'il fallait survoler pour decouvrir qu'ils ne se valent pas.
                    extra: t('ecritures.exportExcelContenu'),
                    onClick: () => exporter('xlsx'),
                  },
                  {
                    key: 'csv',
                    icon: <FileTextOutlined style={{ color: '#6b7280' }} />,
                    label: t('ecritures.exporterCsv'),
                    extra: t('ecritures.exportCsvContenu'),
                    onClick: () => exporter('csv'),
                  },
                ],
              }}
            >
              <Button
                icon={<DownloadOutlined />}
                size="large"
                type="primary"
                loading={!!exporting}
                style={{ background: GREEN, borderColor: GREEN, ...STYLE_BOUTON_FLOTTANT }}
              >
                {exporting ? t('commun:tableExportButton.preparation') : t('commun:tableExportButton.exporter')}
              </Button>
            </Dropdown>
          </BoutonFlottant>
        </Space>
      </div>

      <div className="cb-card" style={{ padding: 0 }}>
        <Table
          className="cb-table"
          dataSource={ecritures}
          columns={colonnes}
          rowKey="ecritureTrackingId"
          loading={loading}
          scroll={{ x: 1100 }}
          expandable={{
            expandedRowRender: renderLignes,
            rowExpandable: (r) => (r.lignes || []).length > 0,
            expandRowByClick: true,
          }}
          pagination={{
            current: page.courante,
            pageSize: page.taille,
            total: total || 0,
            showSizeChanger: true,
            pageSizeOptions: [25, 50, 100],
            showTotal: (n) => t('ecritures.nbEcritures', { count: n }),
            onChange: (courante, taille) => charger(taille !== page.taille ? 1 : courante, taille),
          }}
          locale={{ emptyText: <Empty description={t('ecritures.aucuneEcriture')} image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
        />
      </div>
    </Layout>
  );
}
